import React, { useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../lib/api";
import SectionHeader from "../components/SectionHeader";
import { toast } from "sonner";
import { Mail, MapPin, Twitter, Linkedin, Youtube } from "lucide-react";

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [news, setNews] = useState("");
  const [newsBusy, setNewsBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setNewsBusy(true);
    try {
      await api.post("/newsletter", { email: news, lang });
      toast.success(t.contact.subscribed);
      setNews("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setNewsBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SectionHeader eyebrow="Contact" title={t.sections.contact_title} subtitle={t.sections.contact_sub} />
      <div className="max-w-[1200px] mx-auto px-5 lg:px-10 py-16 grid lg:grid-cols-12 gap-12">
        <form onSubmit={submit} className="lg:col-span-7 grid gap-4" data-testid="contact-form">
          <Field label={t.contact.name} value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="contact-name" />
          <Field label={t.contact.email} type="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} testId="contact-email" />
          <Field label={t.contact.subject} value={form.subject} onChange={(v) => setForm((f) => ({ ...f, subject: v }))} testId="contact-subject" />
          <div>
            <label className="label-eyebrow block mb-2">{t.contact.message}</label>
            <textarea
              required
              rows={6}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              className="w-full bg-transparent border border-slate200 px-3 py-2 text-[14px] focus:border-ink outline-none"
              data-testid="contact-message"
            />
          </div>
          <button
            data-testid="contact-submit"
            disabled={busy}
            className="bg-ink text-white h-11 px-5 text-[12px] uppercase tracking-[0.2em] font-medium hover:bg-ink/90 disabled:opacity-50 self-start"
          >
            {t.contact.send}
          </button>
        </form>

        <aside className="lg:col-span-5 grid gap-8">
          <div className="border border-slate200 p-6">
            <div className="label-eyebrow text-ink mb-4">{t.contact.address_h}</div>
            <div className="text-[14px] text-slate700 flex items-start gap-2 leading-relaxed">
              <MapPin className="w-4 h-4 mt-1 shrink-0" />
              <span>Brussels · Geneva · Paris<br />Hybrid research network</span>
            </div>
            <div className="label-eyebrow text-ink mt-6 mb-3">{t.contact.email_h}</div>
            <a href="mailto:contact@mesc-ce.org" className="text-[14px] text-ink link-underline inline-flex items-center gap-2">
              <Mail className="w-4 h-4" /> contact@mesc-ce.org
            </a>
            <div className="label-eyebrow text-ink mt-6 mb-3">{t.contact.social_h}</div>
            <div className="flex items-center gap-2">
              <a href="#" className="p-2 border border-slate200 hover:bg-ink hover:text-white transition-colors" aria-label="twitter">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="#" className="p-2 border border-slate200 hover:bg-ink hover:text-white transition-colors" aria-label="linkedin">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="#" className="p-2 border border-slate200 hover:bg-ink hover:text-white transition-colors" aria-label="youtube">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="border border-slate200 p-6">
            <div className="label-eyebrow text-ink mb-3">{t.contact.newsletter_h}</div>
            <p className="text-[13px] text-slate600 mb-4">{t.contact.newsletter_sub}</p>
            <form onSubmit={subscribe} className="flex border border-slate200">
              <input
                type="email"
                required
                value={news}
                onChange={(e) => setNews(e.target.value)}
                placeholder={t.contact.newsletter_placeholder}
                className="flex-1 bg-transparent px-3 h-10 text-[13px] outline-none"
                data-testid="newsletter-input"
              />
              <button
                disabled={newsBusy}
                data-testid="newsletter-submit"
                className="px-4 bg-ink text-white text-[12px] uppercase tracking-[0.2em] font-medium hover:bg-ink/90 disabled:opacity-50"
              >
                {t.contact.subscribe}
              </button>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-slate200 px-3 h-10 text-[14px] focus:border-ink outline-none"
        data-testid={testId}
      />
    </div>
  );
}
